var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Logger } from "./Logger";
import { Postman } from "./Postman";
import { AuviousError } from "./AuviousError";
// import { Promise } from "es6-promise";
import { OAuth2Client } from "./auth";
/**
 * Api resource implementation for auvious' services.
 * This instance should be used for saving & reading on a specific resource over HTTP.
 */
export class ApiResource {
    /**
     * Gets the parent client of the api resource.
     */
    get parentClient() {
        return this._client;
    }
    /**
     * Gets the resource default route.
     * This is what is this._appended on the server url, before any route parameters.
     *
     * .eg an api resource of route '/api/user' will get from http://server.url/api/user
     */
    get route() {
        return this._route;
    }
    /**
     * Gets the resource default version.
     * This should be set in every request's X-REQUESTED-VERSION header.
     */
    get version() {
        return this._version;
    }
    constructor(client, route, version = "1.0") {
        this.log = new Logger("ApiResource: " + route);
        if (route) {
            if (route[0] !== "/") {
                route = "/" + route;
            }
            if (route[route.length - 1] === "/") {
                route = route.substring(0, route.length - 1);
            }
        }
        this._client = client;
        this._route = route;
        this._version = version;
        this._postman = new Postman({
            baseURL: this.parentClient.serverUrl,
            headers: {
                "X-REQUESTED-VERSION": this.version,
            },
            tokenRoute: () => {
                return OAuth2Client.TOKEN_REFRESH_ROUTE;
            },
            onRefreshTokenExpired: () => {
                return this.parentClient.auth.refreshTokenExpired();
            },
            onRefreshToken: () => {
                return this.parentClient.auth.refreshTokenRequested();
            },
        });
    }
    getGeneric(ResourceTctor, config = {}) {
        this._appendReadHeaders(config);
        return this._apiCall(ResourceTctor, Postman.GET, config);
    }
    /**
     * NOT WORKING. Have to have a generic for array typed response
     */
    // public getArrayGeneric<ResourceT extends ApiResourceModelArray>(
    //   ResourceTctor: new (args: any) => ResourceT,
    //   config: any = {}
    // ): Promise<any> {
    //   this._appendReadHeaders(config);
    //   return this._apiCall(ResourceTctor, Postman.GET, config);
    // }
    /**
     * Just GET the god-damn thing.
     *
     * @param config axios compatible config
     * @returns http response body
     */
    get(config = {}) {
        return this.getGeneric(null, config);
    }
    getPagedGeneric(ResourceTctor, page, size, config = {}) {
        if (!config.params) {
            config.params = {};
        }
        config.params.page = page;
        config.params.size = size;
        this._appendReadHeaders(config);
        return this._apiCall(ResourceTctor, Postman.GET, config);
    }
    getPaged(page, size, config = {}) {
        return this.getPagedGeneric(null, page, size, config);
    }
    getByIdGeneric(ResourceTctor, id, config = {}) {
        config.urlPostfix = id;
        this._appendReadHeaders(config);
        return this._apiCall(ResourceTctor, Postman.GET, config);
    }
    getById(id, config = {}) {
        return this.getByIdGeneric(null, id, config);
    }
    createGeneric(ResourceTctor, data, config = {}) {
        config.data = data || {};
        this._appendWriteHeaders(config);
        return this._apiCall(ResourceTctor, Postman.POST, config);
    }
    create(data, config = {}) {
        return this.createGeneric(null, data, config);
    }
    updateGeneric(ResourceTctor, data, config = {}) {
        config.data = data || {};
        this._appendWriteHeaders(config);
        return this._apiCall(ResourceTctor, Postman.PUT, config);
    }
    update(data, config = {}) {
        return this.updateGeneric(null, data, config);
    }
    updateByIdGeneric(ResourceTctor, id, data, config = {}) {
        config.urlPostfix = id;
        return this.updateGeneric(ResourceTctor, data, config);
    }
    updateById(id, data, config = {}) {
        return this.updateByIdGeneric(null, id, data, config);
    }
    deleteGeneric(ResourceTctor, config = {}) {
        this._appendWriteHeaders(config);
        return this._apiCall(ResourceTctor, Postman.DELETE, config);
    }
    delete(config = {}) {
        return this.deleteGeneric(null, config);
    }
    deleteByIdGeneric(ResourceTctor, id, config = {}) {
        config.urlPostfix = id;
        return this.deleteGeneric(ResourceTctor, config);
    }
    deleteById(id, config = {}) {
        return this.deleteByIdGeneric(null, id, config);
    }
    _appendReadHeaders(config) {
        // Not currently needed
    }
    _appendWriteHeaders(config) {
        // Not currently needed
    }
    _apiCall(ResourceTctor, method, config, retries = 3, initialDelay = 500) {
        return __awaiter(this, void 0, void 0, function* () {
            config.method = method;
            if (!config.url) {
                if (!config.urlPostfix) {
                    config.url = this._route;
                }
                else {
                    if (config.urlPostfix[0] === "/") {
                        config.url = this._route + config.urlPostfix;
                    }
                    else {
                        config.url = this._route + "/" + config.urlPostfix;
                    }
                }
            }
            // append common headers
            if (this.parentClient.auth.isAuthenticated) {
                if (!config.headers) {
                    config.headers = {};
                }
                config.headers.authorization = this.parentClient.auth.token.bearer;
            }
            for (let attempt = 0; attempt < retries; attempt++) {
                try {
                    const res = yield this._postman.request(config);
                    this.log.info("request " + method, config, "responded with", res);
                    const responseBody = res.data;
                    if (ResourceTctor) {
                        if (typeof responseBody !== "object") {
                            this.log.warn("request " + method, config, " rejected: 404, res body is no object");
                            throw new AuviousError({
                                code: "NOT_FOUND",
                                status: 404,
                                message: "resource requested not found.",
                            });
                        }
                        return new ResourceTctor(responseBody);
                    }
                    else {
                        return responseBody;
                    }
                }
                catch (err) {
                    this.log.warn("request " + method, config, " rejected: ", err);
                    // Check if the error is retryable
                    const status = err.response ? err.response.status : null;
                    const isRetryable = !err.response || (status >= 500 && status < 600) || status === 429;
                    if (!isRetryable || attempt === retries - 1) {
                        throw err;
                    }
                    // Exponential backoff delay with jitter
                    const delay = initialDelay * Math.pow(2, attempt);
                    const jitter = delay * (0.5 + Math.random() * 0.5); // Adds a jitter between 50% and 100% of the delay
                    yield new Promise((resolve) => setTimeout(resolve, jitter));
                }
            }
        });
    }
}
//# sourceMappingURL=ApiResource.js.map